import { Check, Minus, Building2 } from "lucide-react";
import { PLAN_COPY, ENTERPRISE, fmtRM } from "@/config/plans";
import { useApp } from "@/lib/i18n";
import { EnterpriseDialog } from "./EnterpriseDialog";

export const CycleToggle = ({ value, onChange }) => {
  const { L } = useApp();
  const opt = (k, ms, en) => (
    <button type="button" onClick={() => onChange(k)} aria-pressed={value === k} data-testid={`billing-cycle-${k}`}
      className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${value === k ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>{L(ms, en)}</button>
  );
  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1">
      {opt("monthly", "Bulanan", "Monthly")}
      {opt("yearly", "Tahunan", "Yearly")}
      <span className="px-2 text-xs font-bold text-emerald-600">{L("Jimat 2 bulan", "Save 2 months")}</span>
    </div>
  );
};

const Feature = ({ text, off }) => (
  <li className={`flex gap-2 ${off ? "text-slate-400" : ""}`}>
    {off ? <Minus size={15} className="mt-0.5 shrink-0" /> : <Check size={15} className="mt-0.5 shrink-0 text-violet-600" />}<span>{text}</span>
  </li>
);

const Price = ({ p, cycle }) => {
  const { L } = useApp();
  const yearly = cycle === "yearly" && p.price_yearly_myr > 0;
  return (
    <>
      <p className="mt-3 whitespace-nowrap font-heading text-3xl font-extrabold" data-testid={`price-${p.key}`}>
        RM{fmtRM(yearly ? p.price_yearly_myr : p.price_myr)}<span className="text-sm font-medium text-slate-500">/{yearly ? L("tahun", "year") : L("bulan", "month")}</span>
      </p>
      <p className="h-4 text-xs text-slate-500">
        {yearly ? L(`≈ RM${(p.price_yearly_myr / 12).toFixed(2)}/bulan`, `≈ RM${(p.price_yearly_myr / 12).toFixed(2)}/month`) : ""}
      </p>
    </>
  );
};

const Quota = ({ p }) => {
  const { L } = useApp();
  const rows = p.key === "free"
    ? [[L("1 transkripsi", "1 transcription"), L("seumur hidup", "lifetime")]]
    : [[`${p.minutes_quota.toLocaleString()} ${L("minit", "min")}`, L(`/bulan (${+(p.minutes_quota / 60).toFixed(1)} jam, rakaman & muat naik)`.replace(".0 ", " "), `/month (${+(p.minutes_quota / 60).toFixed(1)} h, recording & upload)`)], [`${p.ai_credits} ${L("dokumen", "documents")}`, L("/bulan", "/month")], [`${p.max_members} ${L("ahli", p.max_members > 1 ? "members" : "member")}`, ""]];
  return (
    <div className="mt-4 space-y-1 rounded-xl bg-slate-50 p-3 text-sm" data-testid={`quota-${p.key}`}>
      {rows.map(([a, b]) => <p key={a}><strong>{a}</strong> <span className="text-slate-500">{b}</span></p>)}
    </div>
  );
};

export const PlanCards = ({ plans, cycle, current, action }) => {
  const { L } = useApp();
  return (
    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5" data-testid="plan-cards">
      {plans.map((p) => {
        const c = PLAN_COPY[p.key] || { name: [p.name, p.name], tagline: ["", ""], features: [] };
        const hi = p.key === "pro";
        return (
          <div key={p.key} className={`relative flex flex-col rounded-2xl border bg-white p-6 ${current === p.key ? "border-violet-500 ring-1 ring-violet-500" : hi ? "border-violet-300 shadow-xl shadow-violet-600/10" : "border-slate-200"}`} data-testid={`plan-${p.key}`}>
            {hi && <span className="absolute -top-3 left-6 rounded-full bg-violet-600 px-3 py-1 text-xs font-bold text-white">{L("Paling popular", "Most popular")}</span>}
            <h3 className="text-lg font-bold">{L(...c.name)}</h3>
            <p className="mt-1 min-h-[2.5rem] text-sm text-slate-500">{L(...c.tagline)}</p>
            <Price p={p} cycle={cycle} />
            <Quota p={p} />
            <ul className="mt-5 flex-1 space-y-2 text-sm">
              {c.features.map((f) => <Feature key={f[0]} text={L(...f)} />)}
              {(c.excluded || []).map((f) => <Feature key={f[0]} text={L(...f)} off />)}
            </ul>
            {action(p)}
          </div>
        );
      })}
      <div className="flex flex-col rounded-2xl border border-slate-900 bg-slate-900 p-6 text-white" data-testid="plan-enterprise">
        <h3 className="flex items-center gap-2 text-lg font-bold"><Building2 size={18} /> {L(...ENTERPRISE.name)}</h3>
        <p className="mt-1 min-h-[2.5rem] text-sm text-slate-300">{L(...ENTERPRISE.tagline)}</p>
        <p className="mt-3 font-heading text-3xl font-extrabold">{L(...ENTERPRISE.price)}<span className="text-sm font-medium text-slate-400">/{L("bulan", "month")}</span></p>
        <p className="h-4 text-xs text-slate-400">{L("Harga tersuai", "Custom pricing")}</p>
        <ul className="mt-5 flex-1 space-y-2 text-sm">
          {ENTERPRISE.features.map((f) => <li key={f[0]} className="flex gap-2"><Check size={15} className="mt-0.5 shrink-0 text-violet-300" />{L(...f)}</li>)}
        </ul>
        <EnterpriseDialog />
      </div>
    </div>
  );
};
